import React, { useContext, useState } from "react";
import { BACKEND_URL } from "../../../config/config";
import axios from "axios";
import { Alert, Card, Divider, Space, Upload, Spin, Typography, Progress, Button } from "antd";
import { UploadOutlined, InfoCircleOutlined, DownloadOutlined } from "@ant-design/icons";
import { NotificationContext } from "../../context/NotificationWrapper";

const { Title, Paragraph, Text } = Typography;

function ImportPage() {
  const { openNotification } = useContext(NotificationContext);
  const [loading, setLoading] = useState(false);
  const [uploadErrors, setUploadErrors] = useState([]);
  const [uploadResult, setUploadResult] = useState(null);
  const [reportId, setReportId] = useState(null);

  const handleUpload = async (options) => {
    const { file, onSuccess, onError } = options;
    
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      setLoading(true);
      setUploadErrors([]);
      setUploadResult(null);
      setReportId(null);
      
      const response = await axios.post(
        `${BACKEND_URL}/api/csv/post`,
        formData,
        {
          withCredentials: true,
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      
      onSuccess("OK");
      
      // Traiter les données de réponse
      if (response.data && response.data.data) {
        const importedCount = parseInt(response.data.data[0]);
        const errorsArray = [...response.data.data];
        errorsArray.shift(); // Supprimer le premier élément qui contient le nombre d'adhérents importés
        
        if (errorsArray.length > 0) {
          setUploadErrors(errorsArray);
        }
        
        setUploadResult({
          processed: importedCount,
          total: importedCount + errorsArray.length // Nombre total est la somme des succès et des erreurs
        });
        
        if (response.data.reportId) {
          setReportId(response.data.reportId);
        }
        
        openNotification(
          "Succès",
          `Le fichier a été traité : ${importedCount} adhérent(s) importé(s) avec succès`,
          "success"
        );
      }
    } catch (error) {
      onError("Erreur d'upload");
      
      if (error.response && error.response.data) {
        if (typeof error.response.data === 'string') {
          setUploadErrors([error.response.data]);
        } else if (error.response.data.error) {
          setUploadErrors(error.response.data.error);
        } else {
          setUploadErrors(["Une erreur est survenue lors du traitement du fichier"]);
        }
      } else {
        openNotification(
          "Erreur",
          "Une erreur est survenue lors du traitement du fichier",
          "error"
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${BACKEND_URL}/api/csv/export-template`,
        {
          withCredentials: true,
          responseType: "blob",
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "modele_import_adherents.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      openNotification(
        "Erreur",
        "Impossible de télécharger le modèle de fichier",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadReport = async () => {
    if (!reportId) {
      openNotification(
        "Information",
        "Aucun rapport disponible pour ce traitement",
        "info"
      );
      return;
    }
    
    try {
      setLoading(true);
      const response = await axios.get(
        `${BACKEND_URL}/api/csv/download-import-report/${reportId}`,
        {
          withCredentials: true,
          responseType: "blob",
        }
      );
      
      // Le backend renvoie toujours un CSV pour le rapport
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      
      // Extraire le nom du fichier de l'en-tête Content-Disposition si disponible
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'rapport_import_adherents.csv';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename=["']?([^"';\n]*)["']?/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1];
        }
      }
      
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      openNotification(
        "Succès",
        "Rapport téléchargé avec succès",
        "success"
      );
    } catch (error) {
      console.error("Erreur lors du téléchargement du rapport:", error);
      
      // Gérer les cas d'erreur HTTP
      let errorMessage = "Impossible de télécharger le rapport d'importation";
      
      if (error.response) {
        if (error.response.status === 404) {
          errorMessage = "Le rapport n'existe plus ou a été supprimé";
        } else if (error.response.status === 400) {
          errorMessage = "Format de rapport invalide";
        }
      }
      
      openNotification(
        "Erreur",
        errorMessage,
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="block-right">
      <Spin spinning={loading} tip="Traitement en cours...">
        <Card className="import-card">
          <Title level={2}>Import des adhérents</Title>
          
          <Alert
            type="info"
            icon={<InfoCircleOutlined />}
            showIcon
            message="Guide d'utilisation"
            description={
              <div>
                <Paragraph>
                  Cette page vous permet d'importer en masse des adhérents via un fichier CSV.
                </Paragraph>
                <Paragraph>
                  Le fichier doit respecter un format précis avec les colonnes suivantes :
                </Paragraph>
                <ul>
                  <li><Text strong>companyStatus</Text> - Statut de l'entreprise adhérente</li>
                  <li><Text strong>siren</Text> - Numéro SIREN (obligatoire)</li>
                  <li><Text strong>naf</Text> - Code NAF</li>
                  <li><Text strong>membershipCode</Text> - Code Adhérent (obligatoire)</li>
                  <li><Text strong>companyName</Text> - Raison Sociale (obligatoire)</li>
                  <li><Text strong>address1</Text> - Adresse 1</li>
                  <li><Text strong>address2</Text> - Adresse 2</li>
                  <li><Text strong>address3</Text> - Adresse 3</li>
                  <li><Text strong>address4</Text> - Adresse 4</li>
                  <li><Text strong>zipcode</Text> - Code Postal</li>
                  <li><Text strong>city</Text> - Ville</li>
                  <li><Text strong>nic</Text> - NIC (facultatif, max 5 chiffres)</li>
                </ul>
                <Paragraph>
                  Le système traitera toutes les lignes du fichier et créera ou mettra à jour les adhérents correspondants.
                </Paragraph>
                <Paragraph>
                  Téléchargez le modèle de fichier pour vous assurer de respecter le format requis.
                </Paragraph>
              </div>
            }
            style={{ marginBottom: 24 }}
          />

          {uploadErrors.length > 0 && (
            <Alert
              type="error"
              message="Erreurs lors du traitement du fichier"
              description={
                <ul>
                  {uploadErrors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              }
              showIcon
              closable
              style={{ marginBottom: 24 }}
              action={
                reportId && (
                  <Button 
                    size="small"
                    type="primary"
                    onClick={handleDownloadReport}
                  >
                    Télécharger le rapport
                  </Button>
                )
              }
            />
          )}

          {uploadResult && (
            <div style={{ marginBottom: 24 }}>
              <Title level={4}>Résultat du traitement</Title>
              <div style={{ marginBottom: 16 }}>
                <Progress 
                  percent={uploadResult.total ? Math.round((uploadResult.processed / uploadResult.total) * 100) : 0} 
                  status={uploadErrors.length > 0 ? "exception" : "success"}
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text>Adhérents importés: <Text strong>{uploadResult.processed}</Text> sur <Text strong>{uploadResult.total}</Text> lignes traitées</Text>
                <Text>Statut: <Text strong type={uploadErrors.length > 0 ? "warning" : "success"}>
                  {uploadErrors.length > 0 ? "Terminé avec avertissements" : "Terminé avec succès"}
                </Text></Text>
              </div>
            </div>
          )}

          <Space direction="vertical" style={{ width: '100%' }}>
            <Divider orientation="left">Importer un fichier CSV</Divider>
            <Space size="middle">
              <Upload
                name="file"
                customRequest={handleUpload}
                showUploadList={true}
                accept=".csv"
                maxCount={1}
              >
                <Button icon={<UploadOutlined />} type="primary">
                  Sélectionner un fichier CSV
                </Button>
              </Upload>
              <Button 
                  icon={<DownloadOutlined />} 
                  onClick={handleDownloadTemplate}
                >
                  Télécharger le modèle de fichier
              </Button>
              {reportId && (
                <Button 
                  icon={<DownloadOutlined />} 
                  onClick={handleDownloadReport}
                >
                  Télécharger le rapport
                </Button>
              )}
            </Space>
          </Space>
        </Card>
      </Spin>
    </div>
  );
}

export default ImportPage;